import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Segment } from 'tea-component';
import { handleMentionData, supplementSingleData } from '@/pages/opinion/utils';
import EChart from '@op/pages/community/hashtag/components/EChart';
import CardCommon from '@/pages/opinion/components/CardCommon';
import useLanguage from '@/pages/opinion/hooks/useLanguage';
import {
  formatChartParamsDate,
  listTimeTitleFunc,
} from '@/pages/opinion/components/ChartCommon/formatter';
import {
  timeLineTypeMap,
  timelineTypeOptions,
} from '../../../community/constants';
import { getLocale } from 'umi';
import {
  percentageFormat,
  reviewsFormat,
  sentimentFormat,
} from '@/pages/opinion/utils/value';

let cancel: (() => void) | null = null;
export default function HeroEChart({
  commonParams,
  heroMap = {},
  getServerDataFunc,
  byChannel,
}: {
  commonParams: any;
  heroMap?: {
    [key: string]: { [key in 'zh-CN' | 'en-US']: string };
  };
  languageType?: string;
  getServerDataFunc: (...param: any) => Promise<any>;
  byChannel?: boolean;
}) {
  const { formatOptionsMessage, formatMessage } = useLanguage();
  const [timelineType, setTimelineType] = useState('mentions');
  const [xAxisData, setXAxisData] = useState<Array<string>>([]);
  const [seriesDataArray, setSeriesDataArray] = useState<Array<any>>([]);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(false);
  const splitText = useRef('#_to_#');
  useEffect(() => {
    if (Object.keys(commonParams).length === 0) return;
    if (loading) {
      cancel?.();
      cancel = null;
    }
    setLoading(true);
    setApiError(false);
    getServerDataFunc({ ...commonParams, timeline_type: timelineType })
      .then((resData: any) => {
        const dateType = commonParams.date_type || 'daily';
        const {
          timeline_of_start_time,
          timeline_of_end_time,
          timeline_mentions,
          timeline_float,
          sorts,
          hashtag_channel_split,
        } = resData || {};
        const data = supplementSingleData(
          timeline_of_start_time,
          timeline_of_end_time,
          (timeline_mentions || timeline_float)?.map(
            (item: { count: any; value: any }) => ({
              ...item,
              count: item.count || item.value,
            }),
          ),
          dateType,
        );
        const needTime = ['daily', 'weekly'].includes(dateType)
          ? 'days'
          : dateType === 'hourly'
          ? 'hourly'
          : 'year';
        let { newArr, timeArr } = handleMentionData(data, needTime);
        newArr?.sort((a, b) => sorts?.indexOf(a.name) - sorts?.indexOf(b.name));
        newArr = newArr?.map((item) => ({
          ...item,
          name: item.name,
          key: item.name,
        }));
        if (byChannel) {
          splitText.current = hashtag_channel_split;
        }
        setXAxisData(timeArr);
        setSeriesDataArray(newArr);
      })
      .catch(() => {
        setXAxisData([]);
        setSeriesDataArray([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [timelineType, commonParams, byChannel]);

  const locale: 'zh-CN' | 'en-US' = getLocale();

  const setSeriesData = useMemo(() => {
    return seriesDataArray.map((item) => ({
      ...item,
      name: heroMap?.[item.name]?.[locale],
    }));
  }, [seriesDataArray, locale]);

  return (
    <CardCommon>
      <>
        <Segment
          value={timelineType}
          onChange={(value) => {
            !loading && setTimelineType(value);
          }}
          className="dbo-hero-segment"
          options={formatOptionsMessage(timelineTypeOptions)}
        />
        <div className="line-echarts">
          <EChart
            dateType={commonParams.date_type || 'daily'}
            loading={loading}
            tooltipFormatterFunc={(param: { axisValue: string }[]) => {
              const letTitle = listTimeTitleFunc(
                param[0]?.axisValue,
                commonParams.date_type,
              );
              const formatValueMap: any = {
                mentions: reviewsFormat,
                negative: reviewsFormat,
                'non-negative': (value: number) =>
                  percentageFormat(value * 100),
                sentiment: (value: number) => {
                  return sentimentFormat(value);
                },
              };
              return formatChartParamsDate({
                params: param,
                title: letTitle,
                rightTitle: formatMessage({
                  id: timeLineTypeMap[timelineType],
                }),
                valueFormat: formatValueMap[timelineType],
              });
            }}
            xAxisData={xAxisData}
            apiError={apiError}
            lineLegendFormatter={(name: string) => name}
            timelineType={timelineType}
            seriesDataArray={setSeriesData}
          ></EChart>
        </div>
      </>
    </CardCommon>
  );
}
